import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  ChefHat, 
  Package, 
  Truck, 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  X, 
  Receipt,
  UtensilsCrossed,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MoneyUtils from '../../../utils/moneyUtils';
import { orderUtils } from '../../../utils/orderUtils';
import { differenceInMinutes, differenceInHours } from 'date-fns';
import { getOrderBillingSummary } from '../../../services/api/restApiAdapter';

const AnimatedStatusLabel = ({ status, label }) => {
  const isFinalized = ['completed', 'delivered', 'cancelled', 'failed'].includes(status?.toLowerCase());
  const isDynamic = !isFinalized;
  const [showDots, setShowDots] = useState(false);

  React.useEffect(() => {
    if (!isDynamic) return;
    let timeoutId;
    const scheduleNext = (currentDotsState) => {
      // 2000ms for dots, 5000ms for status text
      const delay = currentDotsState ? 2000 : 5000;
      timeoutId = setTimeout(() => {
        setShowDots((prev) => {
          const next = !prev;
          scheduleNext(next);
          return next;
        });
      }, delay);
    };

    scheduleNext(false);
    return () => clearTimeout(timeoutId);
  }, [isDynamic]);

  if (!isDynamic) {
    return <span>{label}</span>;
  }

  return (
    <span className="relative inline-flex items-center justify-center">
      {/* Invisible anchor text locking container dimensions so it NEVER shrinks */}
      <span className="invisible opacity-0 select-none whitespace-nowrap pointer-events-none" aria-hidden="true">
        {label}
      </span>

      {/* Cross-fading layer occupying locked space */}
      <span className="absolute inset-0 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {showDots ? (
            <motion.span
              key="dots"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="flex items-center justify-center gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse" />
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-100" />
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-200" />
            </motion.span>
          ) : (
            <motion.span
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="whitespace-nowrap flex items-center justify-center"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </span>
  );
};

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return { 
        label: 'Awaiting Approval', 
        bg: 'bg-amber-50 text-amber-700 border-amber-200', 
        dot: 'bg-amber-500 animate-pulse', 
        icon: <Clock size={13} className="text-amber-600" /> 
      };
    case 'confirmed':
    case 'processing':
      return { 
        label: 'Accepted', 
        bg: 'bg-blue-50 text-blue-700 border-blue-200', 
        dot: 'bg-blue-500', 
        icon: <CheckCircle2 size={13} className="text-blue-600" /> 
      };
    case 'preparing':
      return { 
        label: 'Preparing', 
        bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', 
        dot: 'bg-indigo-500 animate-pulse', 
        icon: <ChefHat size={13} className="text-indigo-600" /> 
      };
    case 'ready_for_pickup':
      return { 
        label: 'Ready for Pickup', 
        bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', 
        dot: 'bg-emerald-500 animate-ping', 
        icon: <Package size={13} className="text-emerald-600" /> 
      };
    case 'out_for_delivery':
      return { 
        label: 'Out for Delivery', 
        bg: 'bg-cyan-50 text-cyan-700 border-cyan-200', 
        dot: 'bg-cyan-500', 
        icon: <Truck size={13} className="text-cyan-600" /> 
      };
    case 'completed':
    case 'delivered':
      return { 
        label: 'Completed', 
        bg: 'bg-green-50 text-green-700 border-green-200', 
        dot: 'bg-green-500', 
        icon: <CheckCircle2 size={13} className="text-green-600" /> 
      };
    case 'cancelled':
    case 'failed':
      return { 
        label: 'Cancelled', 
        bg: 'bg-red-50 text-red-700 border-red-200', 
        dot: 'bg-red-500', 
        icon: <XCircle size={13} className="text-red-600" /> 
      };
    default:
      return { 
        label: status || 'Active', 
        bg: 'bg-gray-100 text-gray-700 border-gray-200', 
        dot: 'bg-gray-400', 
        icon: <Clock size={13} className="text-gray-500" /> 
      };
  }
};

const formatTimeElapsed = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const mins = differenceInMinutes(new Date(), date);
    const hours = differenceInHours(new Date(), date);
    if (mins < 1) return 'Just now';
    if (hours > 0) return `${hours}h ${mins % 60}m ago`;
    return `${mins}m ago`;
  } catch {
    return '';
  }
};

const ActiveOrdersSheet = ({
  isOpen,
  onClose,
  orders = [],
  onOpenOrdersScreen,
  theme
}) => {
  const navigate = useNavigate();
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [billingSummaries, setBillingSummaries] = useState({});

  // Filter active orders (pending, preparing, confirmed, ready_for_pickup, out_for_delivery)
  const activeOrders = orders.filter((o) =>
    !['completed', 'cancelled', 'failed', 'delivered'].includes(o.orderStatus?.toLowerCase())
  );

  // If no strictly active orders, show most recent orders (up to 3)
  const displayOrders = activeOrders.length > 0 ? activeOrders : orders.slice(0, 3);
  const isSingleOrder = displayOrders.length === 1;

  // Currently expanded/selected order in list mode or the single order
  const activeOrder = isSingleOrder
    ? displayOrders[0]
    : displayOrders.find((o) => o.id === selectedOrderId) || null;

  // Fetch billing summaries for display orders when sheet is open
  React.useEffect(() => {
    let isMounted = true;
    if (isOpen && displayOrders.length > 0) {
      displayOrders.forEach((ord) => {
        const restId = ord.restaurant_id || ord.restaurantId;
        if (ord.id && restId && !billingSummaries[ord.id]) {
          getOrderBillingSummary(restId, ord.id).then((res) => {
            if (isMounted && res?.success && res?.data) {
              setBillingSummaries((prev) => ({ ...prev, [ord.id]: res.data }));
            }
          }).catch(() => {});
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, displayOrders]);

  const accentColor = theme?.secondary || '#000000';
  const textOnAccent = theme?.primary || '#ffffff';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          />

          {/* Bottom Sheet Modal */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 220 }}
            className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-gray-50 rounded-t-3xl z-[70] max-h-[88vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-white rounded-t-3xl border-b border-gray-100 flex items-center justify-between px-4 pt-3.5 pb-2.5 shrink-0">
              <div className="flex items-center gap-2">
                <div 
                  className="w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs"
                  style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                >
                  <Receipt size={15} />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900 leading-tight">
                    {activeOrders.length > 0 ? 'Active Orders' : 'Recent Orders'}
                  </h2>
                  <p className="text-[10px] text-gray-400 font-medium">
                    {displayOrders.length} {displayOrders.length === 1 ? 'order' : 'orders'} in progress
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-2.5 pb-24">
              {displayOrders.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                  <UtensilsCrossed size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-xs font-semibold">No active orders</p>
                </div>
              ) : isSingleOrder || activeOrder ? (
                /* ─── Single / Selected Order Full Details View ───────────────── */
                <div className="space-y-2.5">
                  {/* Back to list button if selected from multiple */}
                  {!isSingleOrder && (
                    <button
                      onClick={() => setSelectedOrderId(null)}
                      className="text-xs font-bold text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-1.5"
                    >
                      ← Back to active orders list
                    </button>
                  )}

                  {/* Status Banner Card */}
                  {(() => {
                    const statusInfo = getStatusBadge(activeOrder.orderStatus);
                    const isDelivery = activeOrder.orderType?.toLowerCase() === 'delivery';
                    const isTakeaway = activeOrder.orderType?.toLowerCase() === 'takeaway';
                    const shortOrderCode = orderUtils.getShortCode(activeOrder.orderNumber) || activeOrder.id?.slice(0, 6);

                    return (
                      <div className="bg-white rounded-2xl px-2.5 py-2.5 shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-2.5">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] font-bold border shrink-0 ${statusInfo.bg}`}>
                            {statusInfo.icon}
                            <AnimatedStatusLabel status={activeOrder.orderStatus} label={statusInfo.label} />
                          </div>
                          <span className="text-[11px] text-gray-400 font-medium">
                            {formatTimeElapsed(activeOrder.createdAt || activeOrder.created_at)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                          <div>
                            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Order ID</span>
                            <span className="text-xs font-black text-gray-900">
                              #{shortOrderCode}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Service</span>
                            <span className="text-[11px] font-bold text-gray-700 flex items-center justify-end gap-1">
                              {isDelivery ? 'Delivery' : isTakeaway ? 'Takeaway' : `Table ${activeOrder.tableNumber || 'Self'}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Items List */}
                  <div className="bg-white rounded-2xl px-2.5 py-2.5 shadow-sm border border-gray-100">
                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Ordered Items ({(activeOrder.items || []).length})
                    </h3>
                    <div className="divide-y divide-gray-50">
                      {(activeOrder.items || []).map((item, idx) => {
                        const addons = item.addons || item.customizations?.addons || [];
                        return (
                          <div key={idx} className="py-2 first:pt-0 last:pb-0 flex items-start justify-between">
                            <div className="flex-1 pr-2.5">
                              <div className="flex items-center gap-1.5">
                                <span 
                                  className="w-4.5 h-4.5 rounded-md text-[10px] font-black flex items-center justify-center px-1"
                                  style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                                >
                                  {item.quantity}x
                                </span>
                                <span className="text-xs font-bold text-gray-900">
                                  {orderUtils.capitalize(item.name)}
                                </span>
                              </div>
                              {(item.portion?.name || item.portionSize) && (
                                <p className="text-[10px] text-gray-500 ml-6">
                                  Portion: {orderUtils.capitalize(item.portion?.name || item.portionSize)}
                                </p>
                              )}
                              {addons.length > 0 && (
                                <p className="text-[10px] text-gray-400 ml-6">
                                  + {addons.map((a) => orderUtils.capitalize(a.name)).join(', ')}
                                </p>
                              )}
                              {item.cookingInstruction && (
                                <p className="text-[10px] text-orange-600 italic ml-6">
                                  "{orderUtils.capitalize(item.cookingInstruction)}"
                                </p>
                              )}
                            </div>
                            <span className="text-xs font-bold text-gray-800">
                              {MoneyUtils.format(item.price * (item.quantity || 1))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bill Summary */}
                  <div className="bg-white rounded-2xl px-2.5 py-2.5 shadow-sm border border-gray-100 space-y-1.5">
                    <div className="flex justify-between text-[11px] text-gray-500">
                      <span>Item Total</span>
                      <span>{MoneyUtils.format(activeOrder.itemTotal)}</span>
                    </div>
                    {activeOrder.taxesAndCharges > 0 && (
                      <div className="flex justify-between text-[11px] text-gray-500">
                        <span>Taxes & Charges</span>
                        <span>{MoneyUtils.format(activeOrder.taxesAndCharges)}</span>
                      </div>
                    )}
                    {activeOrder.deliveryFee > 0 && (
                      <div className="flex justify-between text-[11px] text-gray-500">
                        <span>Delivery Fee</span>
                        <span>{MoneyUtils.format(activeOrder.deliveryFee)}</span>
                      </div>
                    )}
                    {activeOrder.tipAmount > 0 && (
                      <div className="flex justify-between text-[11px] text-gray-500">
                        <span>Tip</span>
                        <span>{MoneyUtils.format(activeOrder.tipAmount)}</span>
                      </div>
                    )}
                    {activeOrder.discountAmount > 0 && (
                      <div className="flex justify-between text-[11px] text-green-600 font-semibold">
                        <span>Discount</span>
                        <span>-{MoneyUtils.format(activeOrder.discountAmount)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-100 pt-1.5 flex justify-between items-center">
                      <span className="text-xs font-bold text-gray-900">Total Amount</span>
                      <span className="text-xs font-black text-gray-900">
                        {MoneyUtils.format(activeOrder.finalAmount)}
                      </span>
                    </div>
                    <div className="pt-1.5 flex justify-between items-center text-[10px]">
                      <span className="text-gray-400">Payment</span>
                      {(() => {
                        const bSummary = billingSummaries[activeOrder.id];
                        const isSettled = bSummary
                          ? bSummary.settlement?.isSettled
                          : (activeOrder.paymentStatus === 'completed');
                        
                        const rawMethod = (bSummary?.settlement?.primaryPaymentMethod || activeOrder.paymentMethod || 'cod').toLowerCase();
                        let compactMethod = 'COD';
                        if (rawMethod.includes('cash')) compactMethod = 'CASH';
                        else if (rawMethod.includes('upi')) compactMethod = 'UPI';
                        else if (rawMethod.includes('online') || rawMethod.includes('razorpay')) compactMethod = 'ONLINE';
                        else if (rawMethod.includes('split')) compactMethod = 'SPLIT';
                        else if (rawMethod.includes('card')) compactMethod = 'CARD';
                        else compactMethod = rawMethod.toUpperCase();

                        return (
                          <span className={`px-2 py-0.5 rounded-md font-bold uppercase text-[9px] ${
                            isSettled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {isSettled ? 'PAID' : 'UNPAID'} • {compactMethod}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── Multiple Active Orders List View ────────────────────────── */
                <div className="space-y-2.5">
                  {displayOrders.map((order) => {
                    const statusInfo = getStatusBadge(order.orderStatus);
                    const isDelivery = order.orderType?.toLowerCase() === 'delivery';
                    const isTakeaway = order.orderType?.toLowerCase() === 'takeaway';
                    const itemCount = (order.items || []).reduce((acc, it) => acc + (it.quantity || 1), 0);
                    const shortOrderCode = orderUtils.getShortCode(order.orderNumber) || order.id?.slice(0, 6);

                    return (
                      <motion.div
                        key={order.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedOrderId(order.id)}
                        className="bg-white rounded-2xl px-2.5 py-2.5 shadow-sm border border-gray-100 cursor-pointer hover:border-gray-200 transition-all flex flex-col gap-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-gray-900">
                              #{shortOrderCode}
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium">
                              • {formatTimeElapsed(order.createdAt || order.created_at)}
                            </span>
                          </div>
                          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10px] font-bold border shrink-0 ${statusInfo.bg}`}>
                            {statusInfo.icon}
                            <AnimatedStatusLabel status={order.orderStatus} label={statusInfo.label} />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-gray-500 pt-1 border-t border-gray-50">
                          <span>
                            {itemCount} {itemCount === 1 ? 'item' : 'items'} •{' '}
                            {isDelivery ? 'Delivery' : isTakeaway ? 'Takeaway' : `Table ${order.tableNumber || 'Self'}`}
                          </span>
                          <span className="font-bold text-gray-900">
                            {MoneyUtils.format(order.finalAmount)}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer Action */}
            <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto p-3 bg-white/95 backdrop-blur-md border-t border-gray-100 z-10 flex gap-2">
              {activeOrder && (
                <button
                  onClick={() => {
                    const restId = activeOrder.restaurant_id || activeOrder.restaurantId;
                    onClose();
                    if (restId && activeOrder.id) {
                      navigate(`/restaurant/${restId}/orders/${activeOrder.id}`);
                    }
                  }}
                  className="flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 transition-all active:scale-95 cursor-pointer"
                >
                  <FileText size={14} />
                  <span>Order Details</span>
                </button>
              )}

              <button
                onClick={() => {
                  onClose();
                  onOpenOrdersScreen?.();
                }}
                className="flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-all active:scale-95 cursor-pointer"
                style={{ backgroundColor: accentColor, color: textOnAccent }}
              >
                <span>All Orders</span>
                <ArrowRight size={15} />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ActiveOrdersSheet;
